import React from "react";
import { Route, Routes } from "react-router-dom";
import SelectApplicationType from "../../../ui/modules/applications/components/selectApplicationType/SelectApplicationType";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import AdditionalHighDataRoutes from "./highEducation/additionalHighDataRoutes";
import HighEducationRoutes from "./highEducation/highEducationRoutes";
import {
  fetchGetAdditionalEducationDictionaries,
  fetchGetPostgraduateEducationDictionaries,
  fetchGetProgramDictionaries,
} from "../../../store/core/dictionaries/dictionariesThunk";
import Loader from "../../../ui/shared/loaders/Loader";
import { fetchGetAdditionalData } from "../../../store/modules/applications/additionalData/additionalDataThunk";
import { fetchGetAdditionalAEData } from "../../../store/modules/applications/additionalAEData/additionalAEDataThunk";
import AdditionalEducationRoutes from "./additionalEducation/additionalEducationRoutes";
import AdditionalAeDataRoutes from "./additionalEducation/AdditionalAEDataRoutes";
import PostgraduateEducationRoutes from "./postgraduateEducation/postgraduateEducationRoutes";
import AdditionalPostgraduateDataRoutes from "./postgraduateEducation/additionalPostgraduateDataRoutes";
import { fetchGetAdditionalPostgraduateData } from "../../../store/modules/applications/additionalPostgraduateData/additionalPostgraduateDataThunk";
import {
  APPLICATION_ROUTE_PATHS,
  isEducationTypeVisible,
} from "../../constants/admissionDomainConfig";
import BlockedRouteRedirect from "../BlockedRouteRedirect";
import degreeType from "../../types/degreeType";

const renderBlockedApplicationRoutes = (type: degreeType): React.ReactNode => (
  <>
    <Route
      path={`${APPLICATION_ROUTE_PATHS[type].additionalData}/*`}
      element={<BlockedRouteRedirect />}
    />
    <Route
      path={`${APPLICATION_ROUTE_PATHS[type].applications}/*`}
      element={<BlockedRouteRedirect />}
    />
  </>
);

const ApplicationsRoutes: React.FC = () => {
  const dispatch = useAppDispatch();
  const { accountState, dictionariesState } = useAppSelector((state) => state.coreState);
  const { additionalDataState, additionalAEDataState } = useAppSelector(
    (state) => state.modulesState.applicationsState
  );

  React.useEffect(() => {
    if (accountState.user?.hasVO) {
      dispatch(fetchGetProgramDictionaries());
    }
    if (accountState.user?.hasDPO) {
      dispatch(fetchGetAdditionalEducationDictionaries());
    }
    if (accountState.user?.hasASP) {
      dispatch(fetchGetPostgraduateEducationDictionaries());
    }
    dispatch(fetchGetAdditionalData());
    dispatch(fetchGetAdditionalAEData());
    dispatch(fetchGetAdditionalPostgraduateData());
  }, []);

  if (
    dictionariesState.programDictionariesLoadingStatus === "LOADING" ||
    dictionariesState.additionalEducationLoadingStatus === "LOADING" ||
    dictionariesState.postgraduateEducationLoadingStatus === "LOADING" ||
    additionalDataState.dataLoadingStatus === "LOADING" ||
    additionalAEDataState.dataLoadingStatus === "LOADING"
  ) {
    return (
      <Routes>
        <Route path="*" element={<Loader type="spinner" />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="" element={<SelectApplicationType />} />

      {isEducationTypeVisible("high") ? (
        <>
          <Route
            path={`${APPLICATION_ROUTE_PATHS.high.additionalData}/*`}
            element={<AdditionalHighDataRoutes />}
          />
          {accountState?.user?.hasVO &&
            !additionalDataState.educationInfo.transferredFromAnotherUniversity && (
              <Route
                path={`${APPLICATION_ROUTE_PATHS.high.applications}/*`}
                element={<HighEducationRoutes />}
              />
            )}
        </>
      ) : (
        renderBlockedApplicationRoutes("high")
      )}

      {isEducationTypeVisible("additional") ? (
        <>
          <Route
            path={`${APPLICATION_ROUTE_PATHS.additional.additionalData}/*`}
            element={<AdditionalAeDataRoutes />}
          />
          {accountState?.user?.hasDPO && (
            <Route
              path={`${APPLICATION_ROUTE_PATHS.additional.applications}/*`}
              element={<AdditionalEducationRoutes />}
            />
          )}
        </>
      ) : (
        renderBlockedApplicationRoutes("additional")
      )}

      {isEducationTypeVisible("postgraduate") ? (
        <>
          <Route
            path={`${APPLICATION_ROUTE_PATHS.postgraduate.additionalData}/*`}
            element={<AdditionalPostgraduateDataRoutes />}
          />
          {accountState?.user?.hasASP && (
            <Route
              path={`${APPLICATION_ROUTE_PATHS.postgraduate.applications}/*`}
              element={<PostgraduateEducationRoutes />}
            />
          )}
        </>
      ) : (
        renderBlockedApplicationRoutes("postgraduate")
      )}
    </Routes>
  );
};

export default ApplicationsRoutes;
